import styled from '@emotion/styled';
import { Trans, useLingui } from '@lingui/react/macro';

import { VideaTypography } from '../../../../common-components/elements/VideaTypography/VideaTypography';
import { useUniversalToFDI } from '../../../../utils/internationalization/useUniversalToFDI';
import type { ImageObject } from '../../types';

import { useFilteredFindings } from './hooks/useFilteredFindings';

const FindingTable = styled.div`
  display: flex;
  flex-direction: column;
  font-family: monospace;
  padding: 0 0 1em 0;
  .title {
    font-family: 'PT Mono', monospace;
    font-size: 0.75em;
    padding: 1em 0 1em 0;
  }
  .table-wrapper {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    border: 1px solid black;
    border-radius: 0.5em;
    font-size: 0.875em;
  }
  th {
    font-family: 'PT Mono', monospace;
    font-size: 0.7em;
    font-weight: 400;
    text-align: left;
  }
  .finding-cell {
    color: #e23a49;
  }
  table,
  td,
  th {
    border: 1px solid black;
    padding: 1em 2em 1em 2em;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    border-style: hidden;
  }
`;

export const PatientDownloadableFindings = ({
  currentImage,
}: {
  currentImage: ImageObject;
}) => {
  const { displayToothNumber } = useUniversalToFDI();
  const { t, i18n } = useLingui();
  const rblUnit = currentImage?.detectedImageType === 'PERIAPICAL' ? '%' : 'mm';

  const {
    cariesFindings,
    calcFindings,
    rblFindings,
    prlFindings,
    ralFindings,
    showFindings,
  } = useFilteredFindings(currentImage);

  if (!showFindings) {
    return;
  }

  return (
    <div className="videa-findings">
      <VideaTypography>
        <Trans>AI FINDINGS</Trans>
      </VideaTypography>
      {!!cariesFindings?.length && (
        <FindingTable>
          <VideaTypography className="title">
            <Trans>POTENTIAL CAVITIES</Trans>
          </VideaTypography>
          <div className="table-wrapper">
            <table>
              <tr>
                <th>
                  <Trans> TOOTH </Trans>
                </th>
                <th>
                  <Trans> FINDINGS </Trans>
                </th>
              </tr>
              {cariesFindings.map((finding) => {
                return (
                  <tr>
                    <th>{displayToothNumber(finding?.tooth)}</th>
                    <th className="finding-cell">
                      {finding?.lesions}{' '}
                      {finding.lesions > 1 ? t`cavities` : t`cavity`}
                    </th>
                  </tr>
                );
              })}
            </table>
          </div>
        </FindingTable>
      )}
      {!!calcFindings?.length && (
        <FindingTable>
          <VideaTypography className="title">
            <Trans>POTENTIAL CALCULUS FINDINGS</Trans>
          </VideaTypography>
          <div className="table-wrapper">
            <table>
              <tr>
                <th>
                  <Trans> TOOTH </Trans>
                </th>
                <th>
                  <Trans> FINDING </Trans>
                </th>
              </tr>
              {calcFindings?.map((finding) => {
                return (
                  <tr>
                    <th>{displayToothNumber(finding?.tooth)}</th>
                    <th className="finding-cell">
                      <Trans> Calculus Present </Trans>
                    </th>
                  </tr>
                );
              })}
            </table>
          </div>
        </FindingTable>
      )}
      {!!rblFindings?.length && (
        <FindingTable>
          <VideaTypography className="title">
            <Trans>RADIOGRAPHIC BONE LEVELS</Trans>
          </VideaTypography>
          <div className="table-wrapper">
            <table>
              <tr>
                <th>
                  <Trans> TOOTH </Trans>
                </th>
                <th>
                  <Trans> MESIAL </Trans>
                </th>
                <th>
                  <Trans> DISTAL </Trans>
                </th>
              </tr>
              {rblFindings?.map((finding) => {
                const mesial = finding?.rblSides?.find(
                  (side) => side.side === 'mesial',
                )?.distance;
                const distal = finding?.rblSides?.find(
                  (side) => side.side === 'distal',
                )?.distance;
                return (
                  <tr>
                    <th>{displayToothNumber(finding?.tooth)}</th>
                    <th className="finding-cell">
                      {mesial ? `${i18n.number(mesial)} ${rblUnit}` : '--'}
                    </th>
                    <th className="finding-cell">
                      {distal ? `${i18n.number(distal)} ${rblUnit}` : '--'}
                    </th>
                  </tr>
                );
              })}
            </table>
          </div>
        </FindingTable>
      )}
      {!!prlFindings?.length && (
        <FindingTable>
          <VideaTypography className="title">
            <Trans>POTENTIAL ENDODONTIC FINDINGS</Trans>
          </VideaTypography>
          <div className="table-wrapper">
            <table>
              <tr>
                <th>
                  <Trans> TOOTH </Trans>
                </th>
                <th>
                  <Trans> FINDING </Trans>
                </th>
              </tr>
              {prlFindings?.map((finding) => {
                return (
                  <tr>
                    <th>{displayToothNumber(finding?.tooth)}</th>
                    <th className="finding-cell">
                      <Trans> Periapical Radiolucency </Trans>
                    </th>
                  </tr>
                );
              })}
            </table>
          </div>
        </FindingTable>
      )}
      {!!ralFindings?.length && (
        <FindingTable>
          <VideaTypography className="title">
            <Trans>RESTORATIVE IMPERFECTIONS</Trans>
          </VideaTypography>
          <div className="table-wrapper">
            <table>
              <tr>
                <th>
                  <Trans> TOOTH </Trans>
                </th>
                <th>
                  <Trans> FINDING </Trans>
                </th>
              </tr>
              {ralFindings.map((finding) => {
                return (
                  <tr>
                    <th>{displayToothNumber(finding?.tooth)}</th>
                    <th className="finding-cell">
                      {finding?.lesions}{' '}
                      {finding.lesions > 1 ? t`findings` : t`finding`}
                    </th>
                  </tr>
                );
              })}
            </table>
          </div>
        </FindingTable>
      )}
    </div>
  );
};
